import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { FormGroup } from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import {
  Users,
  Home,
  Coins,
  DollarSign,
  Percent,
  Save,
  Loader2
} from 'lucide-react';

import s from './ServiceFeesForm.css';
import CommonFormComponent from '../../CommonField/CommonFormComponent';
import { getCurrencySymbol } from '../../../helpers/currencyConvertion';
import submit from './submit';
import validate from './validate';
import messages from '../../../locale/messages';

class ServiceFeesForm extends Component {

  static propTypes = {
    initialValues: PropTypes.object,
    title: PropTypes.string.isRequired,
    base: PropTypes.string.isRequired,
    availableCurrencies: PropTypes.arrayOf(PropTypes.shape({
      symbol: PropTypes.string.isRequired
    })).isRequired
  };

  render() {
    const { error, handleSubmit, submitting, currentLocale, currency } = this.props;
    const { availableCurrencies, guestType, hostType } = this.props;
    const { formatMessage } = this.props.intl;
    let symbol = getCurrencySymbol(currency, currentLocale);

    return (
      <div className={s.pageContainer}>
        {/* Page Header */}
        <div className={s.pageHeader}>
          <h1 className={s.pageTitle}>
            <FormattedMessage {...messages.manageServiceFee} />
          </h1>
          <p className={s.pageSubtitle}>
            Configure the service fees charged to guests and hosts for each transaction
          </p>
        </div>

        <form onSubmit={handleSubmit(submit)}>
          {error && <div className="alert alert-danger">{formatMessage(error)}</div>}

          {/* Fee Cards Grid */}
          <div className={s.cardsGrid}>
            {/* Guest Fee Card */}
            <div className={s.feeCard}>
              <div className={s.cardHeader}>
                <div className={cx(s.cardHeaderIcon, s.cardHeaderIconGuest)}>
                  <Users size={20} />
                </div>
                <h3 className={s.cardTitle}>
                  <FormattedMessage {...messages.guestLabel} /> Fee
                </h3>
              </div>
              <div className={s.cardBodyStyle}>
                <div className={s.feeTypeLabel}>
                  <FormattedMessage {...messages.feeTypeLabel} />
                </div>
                <div className={s.feeTypeOptions}>
                  <label className={cx(s.feeTypeOption, guestType === 'fixed' && s.feeTypeOptionActive)}>
                    <Field name="guestType" component="input" type="radio" value="fixed" />
                    <DollarSign size={18} />
                    <span className={s.feeTypeOptionLabel}>
                      <FormattedMessage {...messages.fixedPrice} />
                    </span>
                  </label>
                  <label className={cx(s.feeTypeOption, guestType === 'percentage' && s.feeTypeOptionActive)}>
                    <Field name="guestType" component="input" type="radio" value="percentage" />
                    <Percent size={18} />
                    <span className={s.feeTypeOptionLabel}>
                      <FormattedMessage {...messages.percentage} />
                    </span>
                  </label>
                </div>
                <FormGroup style={{ marginBottom: 0 }}>
                  <label className={s.inputLabel}>
                    <FormattedMessage {...messages.serviceFeeLabel} />
                  </label>
                  <Field
                    name="guestValue"
                    type="text"
                    isAddon={true}
                    prefixLabel={guestType === 'fixed' ? symbol : '%'}
                    component={CommonFormComponent}
                    label={formatMessage(messages.serviceFeeLabel)}
                    placeholder="0"
                  />
                </FormGroup>
              </div>
            </div>

            {/* Host Fee Card */}
            <div className={s.feeCard}>
              <div className={s.cardHeader}>
                <div className={cx(s.cardHeaderIcon, s.cardHeaderIconHost)}>
                  <Home size={20} />
                </div>
                <h3 className={s.cardTitle}>
                  <FormattedMessage {...messages.hostLabel} /> Fee
                </h3>
              </div>
              <div className={s.cardBodyStyle}>
                <div className={s.feeTypeLabel}>
                  <FormattedMessage {...messages.feeTypeLabel} />
                </div>
                <div className={s.feeTypeOptions}>
                  <label className={cx(s.feeTypeOption, hostType === 'fixed' && s.feeTypeOptionActive)}>
                    <Field name="hostType" component="input" type="radio" value="fixed" />
                    <DollarSign size={18} />
                    <span className={s.feeTypeOptionLabel}>
                      <FormattedMessage {...messages.fixedPrice} />
                    </span>
                  </label>
                  <label className={cx(s.feeTypeOption, hostType === 'percentage' && s.feeTypeOptionActive)}>
                    <Field name="hostType" component="input" type="radio" value="percentage" />
                    <Percent size={18} />
                    <span className={s.feeTypeOptionLabel}>
                      <FormattedMessage {...messages.percentage} />
                    </span>
                  </label>
                </div>
                <FormGroup style={{ marginBottom: 0 }}>
                  <label className={s.inputLabel}>
                    <FormattedMessage {...messages.serviceFeeLabel} />
                  </label>
                  <Field
                    name="hostValue"
                    type="text"
                    isAddon={true}
                    prefixLabel={hostType === 'fixed' ? symbol : '%'}
                    component={CommonFormComponent}
                    label={formatMessage(messages.serviceFeeLabel)}
                    placeholder="0"
                  />
                </FormGroup>
              </div>
            </div>
          </div>

          {/* Currency Selection */}
          <div className={s.currencyCard}>
            <div className={s.currencyCardHeader}>
              <div className={s.currencyCardIcon}>
                <Coins size={20} />
              </div>
              <h3 className={s.currencyCardTitle}>
                <FormattedMessage {...messages.chooseCurrency} />
              </h3>
            </div>
            <div style={{ maxWidth: '300px' }}>
              <Field
                name="currency"
                component={CommonFormComponent}
                label={formatMessage(messages.chooseCurrency)}
              >
                <option value="">{formatMessage(messages.chooseCurrency)}</option>
                {availableCurrencies.map((curr, key) => {
                  if (curr.isEnable === true) {
                    return <option key={key} value={curr.symbol}>{curr.symbol}</option>
                  }
                  return null;
                })}
              </Field>
            </div>
          </div>

          {/* Footer Actions */}
          <div className={s.footerActions}>
            <button type="submit" className={s.saveButton} disabled={submitting}>
              {submitting ? <Loader2 size={18} className="spin" /> : <Save size={18} />}
              <FormattedMessage {...messages.save} />
            </button>
          </div>
        </form>
      </div>
    );
  }

}

ServiceFeesForm = reduxForm({
  form: 'ServiceFeesForm', // a unique name for this form
  validate
})(ServiceFeesForm);

const selector = formValueSelector('ServiceFeesForm'); // <-- same as form name

const mapState = (state) => ({
  availableCurrencies: state.currency.availableCurrencies,
  base: state.currency.base,
  currentLocale: state.intl.locale,
  currency: selector(state, 'currency'),
  guestType: selector(state, 'guestType'),
  hostType: selector(state, 'hostType'),
});

const mapDispatch = {
};

export default injectIntl(withStyles(s)(connect(mapState, mapDispatch)(ServiceFeesForm)));